import { useState } from 'react';
import { BackControl } from '../components/BackControl.tsx';
import { LoadFailure, isSessionFailure } from '../components/LoadFailure.tsx';
import { useGeocodeBacklog, useUnmappable, useTileCache, useRunGeocoder } from '../api/queries.ts';
import { ApiError } from '../api/client.ts';
import type { GeocodeQueue, GeocodeRow } from '../api/types.ts';

/**
 * The geocoding backlog — where the map's pins come from, and whether they are
 * coming.
 *
 * 048 queues a place when it is created, when its address is corrected, and
 * when a client moves into it. The API drains that queue into coordinates:
 * its worker runs on the `geocoding` job's cadence (src/geocode-worker.ts), and
 * Run now below does the same on demand. It runs in the app, not through Kudu,
 * because only the app's container has the managed identity (measured 23
 * September). The single most useful thing this page does is still say when it
 * last ran, in words, at the top.
 *
 * **Administration only.** It lists client home addresses in bulk, which is a
 * different exposure from a case manager opening one client they hold, so the
 * route refuses anyone without the `geocoding` permission (056) and this page
 * reports that refusal rather than rendering an empty table.
 */

const STATES = [
  'All',
  'Queued',
  'In flight',
  'Failed, will retry',
  'Given up',
  'Resolved',
] as const;

/** Never a bare colour: the state is also a word, for the same reason flags are. */
function StateChip({ state }: { state: string }) {
  const tone =
    state === 'Resolved'
      ? 'bg-emerald-100 text-emerald-900'
      : state === 'Given up'
        ? 'bg-rose-100 text-rose-900'
        : state === 'Failed, will retry'
          ? 'bg-amber-100 text-amber-900'
          : 'bg-neutral-200 text-neutral-800';
  return (
    <span className={`inline-block rounded px-2 py-0.5 text-xs font-semibold ${tone}`}>
      {state}
    </span>
  );
}

function Row({ row }: { row: GeocodeRow }) {
  return (
    <tr className="border-b border-neutral-200 align-top">
      <td className="px-3 py-2">
        <div className="font-semibold text-neutral-900">
          {row.place_name ?? row.address}
        </div>
        {/* The address is the thing that was actually looked up, so it is shown
            even when there is a name — a name that matches and an address that
            does not is exactly the case somebody needs to see. */}
        <div className="text-sm text-neutral-600">{row.address}</div>
        {row.failed_reason && (
          <div className="mt-1 text-sm text-rose-800">{row.failed_reason}</div>
        )}
      </td>
      <td className="px-3 py-2 text-sm text-neutral-700">{row.place_kind}</td>
      <td className="px-3 py-2 text-right tabular-nums">{row.clients_here}</td>
      <td className="px-3 py-2">
        <StateChip state={row.state} />
        {row.attempts > 0 && row.state !== 'Resolved' && (
          <div className="mt-1 text-xs text-neutral-500">
            {row.attempts} attempt{row.attempts === 1 ? '' : 's'}
          </div>
        )}
      </td>
      <td className="px-3 py-2 text-sm text-neutral-700">
        {row.resolved_at ? (
          <>
            <div className="tabular-nums">
              {Number(row.latitude).toFixed(5)}, {Number(row.longitude).toFixed(5)}
            </div>
            {/* Confidence is shown, never averaged away: a Low match is often
                the street or the postcode centroid rather than the building,
                and a case manager driving to it should know that. */}
            <div className="text-xs text-neutral-500">
              {row.match_confidence ?? 'confidence not recorded'}
              {row.provider ? ` · ${row.provider}` : ''}
            </div>
          </>
        ) : (
          <span className="text-neutral-600">—</span>
        )}
      </td>
    </tr>
  );
}

/** When a run started, in the reader's own zone. A timestamptz is an instant, so this conversion is right. */
function startedWhen(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit',
  });
}

/**
 * The geocoder's status, in words, first. The API decides what it says
 * (geocoder-status.ts): a run in progress wins over the last completed state,
 * so the page can never say "never run" above "a run is already under way".
 */
function StatusBanner({ status }: { status: GeocodeQueue['status'] }) {
  const tone =
    status.tone === 'running'
      ? 'border-sky-500 bg-sky-50 text-sky-950'
      : status.tone === 'ok'
        ? 'border-emerald-500 bg-emerald-50 text-emerald-900'
        : 'border-amber-500 bg-amber-50 text-amber-900';
  return (
    <div role="status" className={`mt-4 rounded-lg border-l-8 px-4 py-3 ${tone}`}>
      <p className="font-semibold">{status.headline}</p>
      {status.startedAt && (
        <p className="mt-1">
          {status.running ? 'Started' : 'That run started'} {startedWhen(status.startedAt)}
          {status.startedBy ? ` by ${status.startedBy}` : ''}.
        </p>
      )}
      {status.lines.map((line) => (
        <p key={line} className="mt-1">{line}</p>
      ))}
    </div>
  );
}

/**
 * Run now. Sends the queued addresses — and only the addresses — to Azure Maps
 * straight away rather than waiting for the worker. Starting a run is all this
 * does; the outcome arrives through the status line above, which is refetched.
 * While a run is under way the button stands down and says so, rather than
 * inviting a second press that could only be refused.
 */
function RunNow({ outstanding, running }: { outstanding: number | null; running: boolean }) {
  const run = useRunGeocoder();
  const err = run.error;
  return (
    <div className="mt-3 flex flex-wrap items-center gap-3">
      <button
        type="button"
        onClick={() => run.mutate()}
        disabled={run.isPending || running || outstanding === null || outstanding === 0}
        className="min-h-11 rounded-md border border-neutral-300 bg-white px-4 font-semibold text-neutral-900 hover:bg-neutral-100 disabled:bg-neutral-100 disabled:text-neutral-600 disabled:cursor-not-allowed"
      >
        {run.isPending ? 'Starting…' : 'Run now'}
      </button>
      <span className="text-sm text-neutral-600">
        {running
          ? 'A run is under way; its progress is above.'
          : run.isSuccess
          ? `Started: ${run.data.queued} place(s) queued. The line above updates as it finishes.`
          : outstanding === null
            ? ''
          : outstanding === 0
            ? 'Nothing is waiting.'
            : 'Sends only the addresses to Azure Maps, about five a second.'}
      </span>
      {err && !running && (
        <span className="text-sm text-rose-800" role="alert">
          {err.message}
          {err instanceof ApiError && err.hint ? ` ${err.hint}` : ''}
        </span>
      )}
    </div>
  );
}

export function GeocodeBacklog() {
  const [state, setState] = useState<string>('All');
  const backlog = useGeocodeBacklog(state);
  const unmappable = useUnmappable();
  const tiles = useTileCache();

  const err = backlog.error;
  if (err && !backlog.data) {
    const status = err instanceof ApiError ? err.status : 0;
    if (status === 403) {
      return (
        <div className="mx-auto max-w-3xl p-6">
          <BackControl />
          <h1 className="mt-4 text-lg font-bold text-neutral-900">Not open to your roles</h1>
          <p className="mt-2 text-neutral-700">{err.message}</p>
          {err instanceof ApiError && err.hint && (
            <p className="mt-1 text-neutral-500">{err.hint}</p>
          )}
        </div>
      );
    }
    return (
      <div className="mx-auto max-w-3xl p-6">
        <LoadFailure
          what="The geocoding backlog"
          error={err}
          onRetry={() => void backlog.refetch()}
          retrying={backlog.isFetching}
          forbiddenIsSession={isSessionFailure(err)}
        />
      </div>
    );
  }

  const data = backlog.data;
  const counts = new Map((data?.by_state ?? []).map((s) => [s.state, s.n]));
  const resolved = counts.get('Resolved') ?? 0;
  const outstanding = (data?.total ?? 0) - resolved;

  return (
    <div className="min-h-dvh bg-page">
      <div className="mx-auto max-w-[96rem] px-4 py-6 lg:px-6">
        <BackControl />

        <h1 className="title-bar mt-3">Geocoding backlog</h1>
        <p className="mt-1 max-w-3xl text-neutral-700">
          Every place a client lives needs a coordinate before it can appear on a map.
          Places are worked busiest first, because one lookup at a facility holding
          twelve people produces twelve pins.
        </p>

        {/*
          THE MOST IMPORTANT THING ON THIS PAGE. A queue that has stopped
          draining looks exactly like a queue that is simply long, so whether
          the worker is current is said in words, first.
        */}
        {data?.status && <StatusBanner status={data.status} />}

        {/* Nothing is said about the queue until it has been read: "Nothing is
            waiting" before the answer arrives is a false statement, not a
            loading state. */}
        <RunNow outstanding={data ? outstanding : null} running={data?.status?.running ?? false} />


        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Figure label="Places done" value={resolved} of={data?.total ?? 0} />
          <Figure label="Still to do" value={outstanding} of={data?.total ?? 0} />
          <Figure label="Given up on" value={counts.get('Given up') ?? 0} of={data?.total ?? 0} />
          <Figure
            label="Clients waiting on a pin"
            value={(data?.by_state ?? [])
              .filter((s) => s.state !== 'Resolved')
              .reduce((n, s) => n + (s.clients ?? 0), 0)}
          />
        </div>

        {/*
          Not a footnote. These clients can never be pinned by draining the
          queue, so a map that simply left them out would look complete and be
          missing people. The map shows them as "location not mappable".
        */}
        {unmappable.data && (
          <div className="mt-4 rounded-lg border border-neutral-300 bg-white p-4">
            <h2 className="text-sm font-bold tracking-[0.12em] text-neutral-500 uppercase">
              Clients a map cannot place, whatever the geocoder does
            </h2>
            <ul className="mt-2 space-y-1 text-neutral-800">
              <li>
                <strong className="tabular-nums">{unmappable.data.no_placement}</strong> with no
                placement recorded at all — there is no address to look up.
              </li>
              <li>
                <strong className="tabular-nums">{unmappable.data.elsewhere_free_text}</strong>{' '}
                on a temporary stay recorded as free text rather than a facility, so there is no
                place row to geocode.
              </li>
            </ul>
            <p className="mt-2 text-sm text-neutral-600">
              Both are shown on the map as <em>location not mappable</em> rather than left off.
            </p>
          </div>
        )}

        {/*
          The basemap tile cache. It lives on this page because this is where
          somebody already comes to find out whether the map's plumbing is
          working — and because a cap nobody can see is a cap nobody notices
          hitting. A directory that only grows is the same shape as the audit
          partitions that ran out on 1 January 2028; eviction runs inline on
          every write, and this is the proof it is happening.
        */}
        {tiles.data && (
          <div className="mt-4 rounded-lg border border-neutral-300 bg-white p-4">
            <h2 className="text-sm font-bold tracking-[0.12em] text-neutral-500 uppercase">
              Basemap tile cache
            </h2>
            <p className="mt-2 text-neutral-800">
              <strong className="tabular-nums">{megabytes(tiles.data.bytes)}</strong> in{' '}
              <strong className="tabular-nums">{tiles.data.files}</strong> tile
              {tiles.data.files === 1 ? '' : 's'}, of a{' '}
              <strong className="tabular-nums">{megabytes(tiles.data.capBytes)}</strong> cap
              {' '}({tiles.data.percentOfCap}% full). Tiles are discarded after{' '}
              {tiles.data.maxAgeDays} days.
            </p>
            <div className="mt-2 h-2 w-full max-w-md overflow-hidden rounded bg-neutral-200">
              <div
                className={`h-full ${tiles.data.percentOfCap > 90 ? 'bg-amber-500' : 'bg-neutral-500'}`}
                style={{ width: `${Math.min(100, tiles.data.percentOfCap)}%` }}
              />
            </div>
            <p className="mt-2 text-sm text-neutral-600">
              {tiles.data.lastSweepIso
                ? `Last eviction removed ${tiles.data.lastSweepEvicted} tile(s), freeing ${megabytes(tiles.data.lastSweepFreedBytes)}.`
                : 'Nothing has needed evicting yet.'}{' '}
              Eviction runs whenever a tile is stored, so there is no job to forget.
            </p>
          </div>
        )}

        <div className="mt-5 flex flex-wrap gap-2">
          {STATES.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setState(s)}
              className={`min-h-[44px] rounded-lg px-4 font-semibold ${
                state === s
                  ? 'bg-neutral-900 text-white'
                  : 'bg-white text-neutral-800 ring-1 ring-neutral-300 hover:bg-neutral-100'
              }`}
            >
              {s}
              {s !== 'All' && counts.has(s) ? ` (${counts.get(s)})` : ''}
            </button>
          ))}
        </div>

        <div className="mt-4 overflow-x-auto rounded-lg bg-white shadow">
          <table className="w-full min-w-[56rem] border-collapse">
            <thead>
              <tr className="border-b-2 border-neutral-300 text-left text-xs font-bold tracking-[0.12em] text-neutral-500 uppercase">
                <th className="px-3 py-2">Place</th>
                <th className="px-3 py-2">Kind</th>
                <th className="px-3 py-2 text-right">Clients</th>
                <th className="px-3 py-2">State</th>
                <th className="px-3 py-2">Coordinate</th>
              </tr>
            </thead>
            <tbody>
              {(data?.rows ?? []).map((row) => (
                <Row key={row.request_id} row={row} />
              ))}
            </tbody>
          </table>

          {/* An empty state only ever from a successful answer, never a failure. */}
          {data && data.rows.length === 0 && (
            <p className="px-4 py-8 text-center text-neutral-500">
              {state === 'All'
                ? 'Nothing has ever been queued for geocoding.'
                : `Nothing is ${state.toLowerCase()}.`}
            </p>
          )}
          {backlog.isPending && (
            <p className="px-4 py-8 text-center text-neutral-600">Loading the backlog…</p>
          )}
        </div>

        {data && data.rows.length < data.total && state === 'All' && (
          <p className="mt-3 text-sm text-neutral-600">
            Showing the first {data.rows.length} of {data.total}. The rest are further down
            the queue — fewer clients behind them, or already done.
          </p>
        )}
      </div>
    </div>
  );
}

/** Bytes as something a person reads. */
function megabytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${Math.round((bytes / (1024 * 1024)) * 10) / 10} MB`;
}

function Figure({ label, value, of }: { label: string; value: number; of?: number }) {
  return (
    <div className="rounded-lg bg-white p-4 shadow">
      <div className="text-xs font-bold tracking-[0.12em] text-neutral-500 uppercase">
        {label}
      </div>
      <div className="mt-1 text-2xl font-bold tabular-nums text-neutral-900">
        {value}
        {of !== undefined && of > 0 && (
          <span className="ml-1 text-base font-normal text-neutral-500">of {of}</span>
        )}
      </div>
    </div>
  );
}
